/**
 * Issue Detail Page Controller - Type-Safe Version
 * Coordinates modules and handles page initialization
 */
import { buildLoginRedirect, getCurrentAuthContext, getCurrentPath, getPageUserId, publicPage } from '../lib/page-runtime';
import { workflowPhaseBadge, workflowUnavailableBadge } from '../lib/status-ui';
import type { IssueBoardEvent, IssueDetail, IssueWorkflowState, OpenSubmissionRequest, SubmissionCard, VcsCommit } from '../types/api';

export const CORE_DETAIL_PROGRESSIVE_DELAY_MS = 2_000;
type BrowserSubmissionTargetType = NonNullable<OpenSubmissionRequest['target_type']>;
type BrowserSubmissionIntent = Omit<OpenSubmissionRequest, 'issue_id'>;

export function getDetailActionVisibility(input: {
  issueStatus: string;
  workflowPhase: IssueWorkflowState['workflow']['phase'] | null;
  canEdit: boolean;
}): { showEdit: boolean; showClose: boolean; showReopen: boolean } {
  if (!input.canEdit) {
    return { showEdit: false, showClose: false, showReopen: false };
  }

  const openActionPhases: Array<IssueWorkflowState['workflow']['phase']> = ['open', 'in_progress', 'pending_review', 'needs_work'];
  const canEditOpenIssue = input.issueStatus === 'open' && (
    input.workflowPhase ? openActionPhases.includes(input.workflowPhase) : true
  );
  const canReopenArchivedIssue = input.issueStatus === 'archived' && (
    input.workflowPhase ? input.workflowPhase !== 'duplicate_closed' : true
  );

  return {
    showEdit: canEditOpenIssue,
    showClose: canEditOpenIssue,
    showReopen: canReopenArchivedIssue,
  };
}

export function getStartDevelopmentLoginRedirect(locationLike: Pick<Location, 'pathname' | 'search'> = window.location): string {
  return buildLoginRedirect(getCurrentPath(locationLike));
}

export function getIssueGovernanceSkillId(issue: Pick<IssueDetail, 'skill_id' | 'related_skill_id'> | null | undefined): number | null {
  const candidates = [issue?.related_skill_id, issue?.skill_id];
  const id = candidates.find((candidate) => typeof candidate === 'number' && Number.isFinite(candidate) && candidate > 0);
  return id ?? null;
}

export function buildBrowserSubmissionIntent(input: {
  targetType: string | null | undefined;
  targetSkillId: string | null | undefined;
  compatibilityNotes?: string | null;
  forkReason?: string | null;
  userId?: number | null;
}): { ok: true; intent: BrowserSubmissionIntent } | { ok: false; error: string; focusId: string } {
  const targetType: BrowserSubmissionTargetType =
    input.targetType === 'version_update' || input.targetType === 'fork'
      ? input.targetType
      : 'new_skill';
  const compatibilityNotes = String(input.compatibilityNotes || '').trim();
  if (targetType === 'new_skill') {
    return { ok: true, intent: { target_type: 'new_skill' } };
  }

  const targetSkillId = Number.parseInt(String(input.targetSkillId || '').trim(), 10);
  if (!Number.isFinite(targetSkillId) || targetSkillId <= 0) {
    return { ok: false, error: 'Choose the target skill before opening this submission', focusId: 'submission-target-skill-id' };
  }

  if (targetType === 'version_update') {
    return {
      ok: true,
      intent: {
        target_type: 'version_update',
        target_skill_id: targetSkillId,
        ...(compatibilityNotes ? { compatibility_notes: compatibilityNotes } : {}),
      },
    };
  }

  const forkReason = String(input.forkReason || '').trim();
  if (!forkReason) {
    return { ok: false, error: 'Add a fork reason before opening this submission', focusId: 'submission-fork-reason' };
  }
  if (input.userId == null) {
    return { ok: false, error: 'Could not resolve the fork owner for this browser session', focusId: 'submission-target-type' };
  }
  return {
    ok: true,
    intent: {
      target_type: 'fork',
      target_skill_id: targetSkillId,
      derived_from_skill_id: targetSkillId,
      proposed_owner_type: 'user',
      proposed_owner_id: String(input.userId),
      fork_reason: forkReason,
      ...(compatibilityNotes ? { compatibility_notes: compatibilityNotes } : {}),
    },
  };
}

function initIssueDetailPage() {
  // Get issue ID from data attribute
  const container = document.getElementById('issue-detail');
  if (!container) return;

  const issueIdStr = container.dataset.issueId;
  if (!issueIdStr) {
    console.error('issue-detail: missing data-issue-id attribute');
    return;
  }

  const issueId = parseInt(issueIdStr, 10);
  if (isNaN(issueId)) {
    console.error('issue-detail: invalid issue ID');
    return;
  }

  function renderIssueAgentGuide(details: HTMLDetailsElement): void {
    const body = details.querySelector<HTMLElement>('[data-issue-agent-guide]');
    if (!body || !details.open || body.textContent) return;
    body.innerHTML =
      '<p>For curl, CLI, or text-only agents, fetch <code>/api/issues/' + issueId + '</code> or MCP <code>get_issue_detail</code>.</p>' +
      '<p>Use <code>acceptance_criteria</code> before starting a submission with <code>submit_repo_to_issue</code>.</p>';
  }

  window.renderIssueAgentGuide = renderIssueAgentGuide;

  // Initialize interactions module
  window.IssueDetailInteractions.init(issueId);

  let currentIssue: IssueDetail | null = null;
  let lastRenderData: {
    issue: IssueDetail;
    workflowState: IssueWorkflowState | null;
    boardEvents: IssueBoardEvent[];
    codeCommits: VcsCommit[];
    submissions: SubmissionCard[];
  } | null = null;

  function setRowVisible(id: string, visible: boolean): void {
    const row = document.getElementById(id);
    if (row) row.style.display = visible ? '' : 'none';
  }

  function updateSubmissionGovernanceControls(): void {
    const select = document.getElementById('submission-target-type') as HTMLSelectElement | null;
    const targetType = select?.value || 'new_skill';
    const requiresTargetSkill = targetType === 'version_update' || targetType === 'fork';
    const isFork = targetType === 'fork';
    const targetSkillInput = document.getElementById('submission-target-skill-id') as HTMLInputElement | null;
    const relatedSkillId = getIssueGovernanceSkillId(currentIssue);

    if (requiresTargetSkill && targetSkillInput && !targetSkillInput.value && relatedSkillId) {
      targetSkillInput.value = String(relatedSkillId);
    }

    setRowVisible('submission-target-skill-row', requiresTargetSkill);
    setRowVisible('submission-governance-notes-row', requiresTargetSkill);
    setRowVisible('submission-fork-reason-row', isFork);
  }

  function bindSubmissionGovernanceControls(): void {
    const select = document.getElementById('submission-target-type') as HTMLSelectElement | null;
    if (!select) return;
    select.addEventListener('change', updateSubmissionGovernanceControls);
    updateSubmissionGovernanceControls();
  }

  function collectSubmissionIntent(auth: ReturnType<typeof getCurrentAuthContext>) {
    const targetType = (document.getElementById('submission-target-type') as HTMLSelectElement | null)?.value || 'new_skill';
    const result = buildBrowserSubmissionIntent({
      targetType,
      targetSkillId: (document.getElementById('submission-target-skill-id') as HTMLInputElement | null)?.value || '',
      compatibilityNotes: (document.getElementById('submission-compatibility-notes') as HTMLTextAreaElement | null)?.value || '',
      forkReason: (document.getElementById('submission-fork-reason') as HTMLTextAreaElement | null)?.value || '',
      userId: getPageUserId(auth.user),
    });
    if (!result.ok) {
      showToast(result.error, 'error');
      document.getElementById(result.focusId)?.focus();
      return null;
    }
    return result.intent;
  }

  // ============================================================================
  // Main Rendering
  // ============================================================================

  function renderDetail(d: IssueDetail, workflowState: IssueWorkflowState | null, boardEvents: IssueBoardEvent[], codeCommits: VcsCommit[], submissions: SubmissionCard[]) {
    currentIssue = d;
    lastRenderData = { issue: d, workflowState, boardEvents, codeCommits, submissions };
    window.IssueDetailInteractions.setCurrentIssue(d);
    const auth = getCurrentAuthContext();
    const isAdmin = !!(auth.user && auth.user.role === 'maintainer');
    const isSubmitter = getPageUserId(auth.user) === d.submitter_id;
    const canEdit = isSubmitter || isAdmin;
    const workflowPhase = workflowState ? workflowState.workflow.phase : null;

    document.getElementById('issue-title')!.textContent = d.title;
    document.getElementById('issue-description')!.textContent = d.description;
    const acceptanceCriteria = String(d.acceptance_criteria || '').trim();
    const criteriaSection = document.getElementById('criteria-section');
    const criteriaContent = document.getElementById('issue-criteria');
    if (criteriaSection && criteriaContent) {
      criteriaContent.textContent = acceptanceCriteria;
      criteriaSection.style.display = acceptanceCriteria ? '' : 'none';
    }
    document.getElementById('issue-status')!.innerHTML = workflowPhase ? workflowPhaseBadge(workflowPhase) : workflowUnavailableBadge();
    const originBadge = document.getElementById('issue-origin');
    if (originBadge) {
      if (d.request_origin === 'official') {
        originBadge.innerHTML = '<span class="inline-flex items-center rounded-full border border-fuchsia-500/30 bg-fuchsia-950/25 px-2 py-0.5 text-[11px] font-medium text-fuchsia-200"><i class="fas fa-certificate mr-1"></i>Official request</span>';
        originBadge.style.display = '';
      } else {
        originBadge.innerHTML = '';
        originBadge.style.display = 'none';
      }
    }
    document.getElementById('vote-count')!.textContent = String(d.vote_count);
    document.getElementById('issue-created')!.textContent = timeAgo(d.created_at);
    document.getElementById('issue-submitter')!.innerHTML = window.IssueDetailRenderers.userLink({
      username: (d as IssueDetail & { submitter_username?: string | null }).submitter_username || d.submitter_name || null,
      label: (d as IssueDetail & { submitter_display_name?: string | null }).submitter_display_name || d.submitter_name || (d as IssueDetail & { submitter_username?: string | null }).submitter_username || 'Unknown',
      type: 'user',
      id: d.submitter_id,
      className: 'text-zinc-400 hover:text-brand-300 transition-colors'
    });

    const actionVisibility = getDetailActionVisibility({
      issueStatus: d.status,
      workflowPhase,
      canEdit,
    });
    ['edit-btn', 'close-btn', 'reopen-btn'].forEach((id) => {
      const actionButton = document.getElementById(id);
      if (actionButton) actionButton.style.display = 'none';
    });
    if (actionVisibility.showEdit) {
      document.getElementById('edit-btn')!.style.display = '';
    }
    if (actionVisibility.showClose) {
      document.getElementById('close-btn')!.style.display = '';
    }
    if (actionVisibility.showReopen) {
      document.getElementById('reopen-btn')!.style.display = '';
    }

    // Render sections
    const nextAction = document.getElementById('issue-next-action');
    if (nextAction) {
      nextAction.innerHTML = window.IssueDetailRenderers.renderIssueNextAction({
        issue: d,
        workflow_state: workflowState,
        submissions,
        loggedIn: auth.loggedIn,
        canRequesterReview: isSubmitter && d.request_origin !== 'official',
        loginHref: buildLoginRedirect(getCurrentPath()),
      });
    }
    document.getElementById('submissions-section')!.innerHTML = window.IssueDetailRenderers.renderSubmissions(submissions, {
      canRequesterReview: isSubmitter && d.request_origin !== 'official',
    });
    const developerWorkspace = document.getElementById('developer-workspace-section');
    const developerWorkspaceContent = document.getElementById('developer-workspace-content');
    if (developerWorkspace && developerWorkspaceContent) {
      developerWorkspaceContent.innerHTML = window.IssueDetailRenderers.renderDeveloperWorkspace({
        issueStatus: d.status,
        loggedIn: auth.loggedIn,
        submissions: submissions as Array<SubmissionCard & { is_mine?: boolean }>,
        loginHref: buildLoginRedirect(getCurrentPath()),
        issue: d,
      });
      developerWorkspace.style.display = '';
      bindSubmissionGovernanceControls();
    }
    document.getElementById('activity-section')!.innerHTML = window.IssueDetailRenderers.renderBoardEvents(boardEvents, codeCommits);

    // Maintainer actions
    const adminActionsHtml = window.IssueDetailRenderers.renderAdminWorkflowActions({
      issue: d,
      workflow_state: workflowState,
      submissions: submissions,
      isAdmin: isAdmin
    });
    const adminContainer = document.getElementById('admin-actions-section');
    if (adminActionsHtml) {
      if (adminContainer) {
        adminContainer.innerHTML = adminActionsHtml;
        adminContainer.style.display = '';
      }
    } else if (adminContainer) {
      adminContainer.innerHTML = '';
      adminContainer.style.display = 'none';
    }

    // Show comment box for authenticated users
    if (getCurrentAuthContext().loggedIn) {
      document.getElementById('comment-box')!.style.display = '';
    }

    // Show content, hide loading
    document.getElementById('issue-loading')?.remove();
    document.getElementById('issue-content')!.style.display = '';
  }

  // ============================================================================
  // Page Initialization
  // ============================================================================


  function setStartDevelopmentBusy(trigger: HTMLElement | null, busy: boolean): void {
    const controls = Array.from(document.querySelectorAll<HTMLButtonElement>('[data-start-development]'));
    const targetControls = trigger instanceof HTMLButtonElement && !controls.includes(trigger)
      ? controls.concat(trigger)
      : controls;
    targetControls.forEach(function(btn) {
      btn.disabled = busy;
      btn.setAttribute('aria-busy', busy ? 'true' : 'false');
      btn.innerHTML = busy
        ? '<i class="fas fa-spinner fa-spin mr-2"></i>Starting work...'
        : '<i class="fas fa-play mr-2"></i>Start work';
    });
  }

  async function startDevelopment(trigger?: HTMLElement) {
    if (!currentIssue) return;
    const auth = getCurrentAuthContext();
    if (!auth.loggedIn) {
      window.location.href = getStartDevelopmentLoginRedirect();
      return;
    }
    const intent = collectSubmissionIntent(auth);
    if (!intent) return;
    setStartDevelopmentBusy(trigger || null, true);
    try {
      const result = await window.IssueDetailData.startSubmission(currentIssue.id, intent);
      if (!result.ok) {
        showToast(result.error || 'Failed to open submission', 'error');
        return;
      }
      window.location.href = '/submissions/' + result.data.submissionId;
    } catch (error) {
      showToast((error as Error).message || 'Failed to open submission', 'error');
    } finally {
      setStartDevelopmentBusy(trigger || null, false);
    }
  }

  window.startDevelopment = startDevelopment;

  async function loadAndRender() {
    let coreRendered = false;
    let fullSettled = false;
    const fullLoadPromise = window.IssueDetailData.loadIssueDetail(issueId);
    const coreDetailTimer = window.setTimeout(() => {
      if (fullSettled || coreRendered) return;

      void window.IssueDetailData.loadCoreIssueDetail(issueId).then((coreResult) => {
        if (fullSettled || coreRendered || !coreResult.ok) return;
        coreRendered = true;
        renderDetail(coreResult.data, null, [], [], []);
      });
    }, CORE_DETAIL_PROGRESSIVE_DELAY_MS);

    try {
      const workflowStateEndpoint = '/workflow-state';
      const issueResult = await fullLoadPromise;
      fullSettled = true;
      window.clearTimeout(coreDetailTimer);

      if (!issueResult.ok) {
        if (coreRendered) return;
        const errorMessage = issueResult.error || ('Failed to load issue (including ' + workflowStateEndpoint + ')');
        document.getElementById('issue-loading')!.innerHTML =
          '<div class="card p-6 text-center">' +
            '<i class="fas fa-exclamation-circle text-4xl text-red-400 mb-4"></i>' +
            '<p class="text-zinc-400">' + window.IssueDetailRenderers.escapeHtml(errorMessage) + '</p>' +
          '</div>';
        return;
      }

      const issue = issueResult.data.detail;
      const workflowState = issueResult.data.workflow_state;
      const boardEvents = issueResult.data.board_events;
      const codeCommits = issueResult.data.code_commits as VcsCommit[];
      const submissions = issueResult.data.submissions;

      renderDetail(issue, workflowState, boardEvents, codeCommits, submissions);
    } catch (error) {
      fullSettled = true;
      window.clearTimeout(coreDetailTimer);
      if (coreRendered) return;
      document.getElementById('issue-loading')!.innerHTML =
        '<div class="card p-6 text-center">' +
          '<i class="fas fa-exclamation-circle text-4xl text-red-400 mb-4"></i>' +
          '<p class="text-zinc-400">Failed to load issue: ' + window.IssueDetailRenderers.escapeHtml((error as Error).message) + '</p>' +
        '</div>';
    }
  }

  window.addEventListener('skillfab:issue-detail:refresh', () => {
    void loadAndRender();
  });

  publicPage({
    init: loadAndRender,
    onAuth: () => {
      if (!lastRenderData) return;
      renderDetail(
        lastRenderData.issue,
        lastRenderData.workflowState,
        lastRenderData.boardEvents,
        lastRenderData.codeCommits,
        lastRenderData.submissions,
      );
    },
    deferUntilCoreReady: false,
  });
}

if (typeof window !== 'undefined' && typeof document !== 'undefined') {
  initIssueDetailPage();
}
